import { ErrorHandlerService } from '@y42/error-handling';
import { Y42FeatureType } from '@y42/json-schema';
import { getY42GitPath, Y42GitFolders } from '../git';
import {
  AbstractFeatureEntityState,
  Noun,
  Y42FeatureTypeToChildLabel,
  Y42FeatureTypeToChildLogo,
  Y42FeatureTypeToLabel,
  Y42FeatureTypeToLogo,
} from '../models';
import { Y42RouterService } from '../routing';
import { AbstractFeatureQueryEntity } from '../state';
import { AbstractFeatureAggregationService, ServiceDefinitionWithName } from './abstract-feature-aggregation.service';
import { AbstractFeatureService } from './abstract-feature.service';
import { FeatureChildAggregationService } from './feature-child-aggregation.service';

export interface FeatureServiceDefinition
  extends ServiceDefinitionWithName<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    AbstractFeatureService<AbstractFeatureEntityState<any>>,
    AbstractFeatureQueryEntity
  > {}

export abstract class FeatureAggregationService extends AbstractFeatureAggregationService<
  AbstractFeatureService,
  AbstractFeatureQueryEntity
> {
  constructor(
    readonly featureChildAggregationService: FeatureChildAggregationService,
    readonly routerService: Y42RouterService,
    private readonly errorHandlerService: ErrorHandlerService,
  ) {
    super(routerService);
  }

  getServiceDefinitionByPath($path: string) {
    const gitFolders = Object.values(Y42GitFolders).sort((a, b) => b.split('/').length - a.split('/').length);
    const gitFolder = gitFolders.find((folder) => $path.includes(folder))!;

    // src/Integrations/integration_one/metadata.json
    // src/Models/SQL/model_one/metadata.json
    const pathElements = $path.replaceAll(getY42GitPath(gitFolder), '').split('/').filter(Boolean);

    // integration_one
    // integration_one/metadata.json
    // model_one
    // model_one/metadata.json
    const isParent =
      pathElements.length && pathElements.length <= 2 && (!pathElements[1] || pathElements[1]?.endsWith('.json'));

    // integration_one/table_one
    // integration_one/table_one/metadata.json
    // model_one/table_one
    // model_one/table_one/metadata.json
    const isChild =
      pathElements.length && pathElements.length <= 3 && (!!pathElements[1] || !pathElements[1]?.endsWith('.json'));

    if (isParent) {
      return {
        ...this.getByGitPath($path)!,
        level: 0,
      };
    } else if (isChild) {
      return {
        ...this.featureChildAggregationService.getByGitPath($path)!,
        level: 1,
      };
    }

    this.errorHandlerService.handleError(
      `[FeatureAggregationService.getServiceDefinitionByPath]: could not find def for path "${$path}"`,
    );
    return null;
  }

  /**
   * Finds the top-level feature's label for the given path, even if the path points to a child entity.
   *
   * Example: `src/Models/SQL/sql-model/table` would return "SQL-Models"
   */
  getRootLabelByPath($path: string, noun?: Noun): string | null {
    const serviceDef = this.getByGitPath($path);
    if (!serviceDef) {
      return null;
    }

    return this.getLabelByFeatureType(serviceDef.featureType, 0, noun);
  }

  /**
   * Finds the top-level feature's logo for the given path, even if the path points to a child entity.
   *
   * Example: `src/Models/SQL/sql-model/table` would return "assets/images/y42-product-logos/y42-model-sql.svg"
   */
  getRootLogoByPath($path: string): string | null {
    const serviceDef = this.getByGitPath($path);
    if (!serviceDef) {
      return null;
    }

    return this.getLogoByFeatureType(serviceDef.featureType);
  }

  /**
   * Finds the feature's label for the given path.
   *
   * Example: `src/Models/SQL/sql-model/table` would return "Tables"
   */
  getLabelByPath($path: string, noun?: Noun): string | null {
    const serviceDef = this.getServiceDefinitionByPath($path);

    if (!serviceDef) {
      return null;
    }

    // `serviceDef.featureType` may be undefined for children entities
    const featureType = this.getByGitPath($path)!.featureType;
    return this.getLabelByFeatureType(featureType, serviceDef.level, noun);
  }

  /**
   * Returns the label for the given feature type.
   */
  getLabelByFeatureType(featureType: Y42FeatureType, level = 0, noun = Noun.Plural): string | null {
    if (level === 0) {
      return Y42FeatureTypeToLabel[featureType]?.[noun];
    } else if (level === 1) {
      return Y42FeatureTypeToChildLabel[featureType]?.[noun] || null;
    }

    return null;
  }

  /**
   * Finds the feature's logo for the given path.
   */
  getLogoByPath($path: string): string | null {
    const serviceDef = this.getServiceDefinitionByPath($path);
    if (!serviceDef) {
      return null;
    }

    const entity = serviceDef.query.getUIEntity($path);
    if (entity?.typeIcon) {
      return entity.typeIcon;
    }

    // `serviceDef.featureType` may be undefined for children entities
    const featureType = this.getByGitPath($path)!.featureType;
    return this.getLogoByFeatureType(featureType, serviceDef.level);
  }

  /**
   * Returns the logo for the given feature type.
   */
  getLogoByFeatureType(featureType: Y42FeatureType, level = 0) {
    if (level === 0) {
      return Y42FeatureTypeToLogo[featureType];
    } else if (level === 1) {
      return Y42FeatureTypeToChildLogo[featureType];
    }

    return null;
  }
}
